/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type RefObject, useCallback } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Field from '@atlaskit/form/field';
import Form from '@atlaskit/form/form';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const styles = cssMap({
	renameFormInner: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
	textFieldContainer: {
		flexGrow: 1,
		minWidth: '0px',
	},
	textFieldStack: {
		width: '100%',
	},
	textField: {
		width: '100%',
		boxSizing: 'border-box',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		paddingTop: token('space.050'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		border: `${token('border.width')} solid ${token('color.border.input')}`,
		borderRadius: token('radius.small'),
		outline: 'none',
		'&:focus': {
			borderColor: token('color.border.focused'),
			outlineColor: token('color.border.focused'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width'),
		},
	},
});

type SpaceRowRenameFormProps = {
	spaceId: string;
	formRef: RefObject<HTMLDivElement>;
	inputRef: RefObject<HTMLInputElement>;
	formXcss: React.ComponentProps<typeof Box>['xcss'];
	hasRenameError: boolean;
	isRenaming: boolean;
	localSpaceName: string;
	onCancelRename: () => void;
	onSubmitRename: (value: string) => Promise<void>;
};

export const SpaceRowRenameForm = ({
	spaceId,
	formRef,
	inputRef,
	formXcss,
	hasRenameError,
	isRenaming,
	localSpaceName,
	onCancelRename,
	onSubmitRename,
}: SpaceRowRenameFormProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const errorId = `browse-spaces-fullscreen-row-name-edit-error-${spaceId}`;

	const handleSubmit = useCallback(
		(data: { spaceName: string }) => {
			void onSubmitRename(data.spaceName);
		},
		[onSubmitRename],
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === 'Escape') {
				onCancelRename();
			}
		},
		[onCancelRename],
	);

	return (
		<Box xcss={formXcss} ref={formRef}>
			<Form onSubmit={handleSubmit}>
				{({ formProps }) => (
					<form {...formProps} css={styles.renameFormInner}>
						<Box xcss={styles.textFieldContainer}>
							<Field name="spaceName" defaultValue={localSpaceName}>
								{({ fieldProps }) => {
									const {
										isDisabled: _isDisabled,
										isInvalid: _isInvalid,
										isRequired: _isRequired,
										...inputFieldProps
									} = fieldProps as typeof fieldProps & { isInvalid?: boolean };

									return (
										<Stack xcss={styles.textFieldStack} space="space.050">
											<input
												{...inputFieldProps}
												ref={inputRef}
												css={styles.textField}
												aria-label={formatMessage(messages.moreMenuRenameLabel)}
												aria-invalid={hasRenameError ? true : undefined}
												aria-errormessage={hasRenameError ? errorId : undefined}
												onKeyDown={handleKeyDown}
												data-testid={`browse-spaces-fullscreen-row-name-edit-${spaceId}`}
											/>
											{hasRenameError ? (
												<Text
													id={errorId}
													color="color.text.danger"
													size="small"
													role="alert"
													testId={`browse-spaces-fullscreen-row-name-edit-error-${spaceId}`}
												>
													{formatMessage(messages.renameErrorMessage)}
												</Text>
											) : null}
										</Stack>
									);
								}}
							</Field>
						</Box>
						<IconButton
							icon={(iconProps) => (
								<CheckMarkIcon {...iconProps} color={token('color.icon.accent.green')} />
							)}
							type="submit"
							appearance="subtle"
							shape="circle"
							spacing="compact"
							isDisabled={isRenaming}
							label={formatMessage(messages.confirmRenameLabel)}
							testId={`browse-spaces-fullscreen-row-rename-confirm-${spaceId}`}
						/>
						<IconButton
							icon={(iconProps) => (
								<CrossIcon {...iconProps} color={token('color.icon.accent.red')} />
							)}
							type="button"
							onClick={onCancelRename}
							appearance="subtle"
							shape="circle"
							spacing="compact"
							isDisabled={isRenaming}
							label={formatMessage(messages.cancelRenameLabel)}
							testId={`browse-spaces-fullscreen-row-rename-cancel-${spaceId}`}
						/>
					</form>
				)}
			</Form>
		</Box>
	);
};
